import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { SEO } from '../../utils/seo';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '株式会社G11',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: '中小企業のM&A・事業承継を専門とする次世代型仲介プラットフォーム',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '二日町12-25-2F',
      addressLocality: '仙台市青葉区',
      addressRegion: '宮城県',
      postalCode: '980-0000',
      addressCountry: 'JP'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@nextage.jp',
      contactType: 'Customer Service',
      availableLanguage: 'Japanese'
    },
    foundingLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: '仙台市',
        addressRegion: '宮城県',
        addressCountry: 'JP'
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="会社概要 | 株式会社G11 | 仙台市のM&A・事業承継専門会社"
        description="株式会社G11の会社概要。宮城県仙台市青葉区に本社を置き、中小企業のM&A・事業承継を専門とする次世代型仲介プラットフォームを運営。資本金2億2,000万円。"
        keywords="会社概要,株式会社G11,仙台,宮城,M&A,事業承継,企業情報"
        jsonLd={jsonLd}
      />
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 text-white py-16 md:py-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>

        {/* Gradient Orbs */}
        <div className="absolute top-0 right-1/4 w-64 md:w-96 h-64 md:h-96 bg-teal-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-64 md:w-96 h-64 md:h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl mb-6 md:mb-8">
              <i className="ri-building-line text-3xl md:text-4xl text-white"></i>
            </div>
            <h1 className="text-3xl md:text-5xl font-black mb-4 md:mb-6">
              会社概要
            </h1>
            <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
              中小企業のM&A・事業承継を専門とする<br className="hidden md:block" />
              次世代型仲介プラットフォーム
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        
        {/* Company Information */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-sm p-6 md:p-10 mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6 md:mb-8 flex items-center gap-3">
            <div className="w-1 h-6 md:h-8 bg-gradient-to-b from-teal-500 to-cyan-500 rounded-full"></div>
            会社情報
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="py-4 md:py-6 pr-4 md:pr-8 text-sm md:text-base font-bold text-gray-700 whitespace-nowrap align-top">会社名</td>
                  <td className="py-4 md:py-6 text-sm md:text-base text-gray-900">株式会社G11</td>
                </tr>
                <tr>
                  <td className="py-4 md:py-6 pr-4 md:pr-8 text-sm md:text-base font-bold text-gray-700 whitespace-nowrap align-top">資本金</td>
                  <td className="py-4 md:py-6 text-sm md:text-base text-gray-900">2億2,000万円</td>
                </tr>
                <tr>
                  <td className="py-4 md:py-6 pr-4 md:pr-8 text-sm md:text-base font-bold text-gray-700 whitespace-nowrap align-top">所在地</td>
                  <td className="py-4 md:py-6 text-sm md:text-base text-gray-900">
                    〒980-0000<br />
                    宮城県仙台市青葉区二日町12-25-2F
                  </td>
                </tr>
                <tr>
                  <td className="py-4 md:py-6 pr-4 md:pr-8 text-sm md:text-base font-bold text-gray-700 whitespace-nowrap align-top">事業内容</td>
                  <td className="py-4 md:py-6 text-sm md:text-base text-gray-900">
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                        <span>中小企業のM&A仲介業務</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                        <span>事業承継コンサルティング</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                        <span>休眠会社・稼働法人の売買仲介</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                        <span>企業価値評価・デューデリジェンス支援</span>
                      </li>
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td className="py-4 md:py-6 pr-4 md:pr-8 text-sm md:text-base font-bold text-gray-700 whitespace-nowrap align-top">営業時間</td>
                  <td className="py-4 md:py-6 text-sm md:text-base text-gray-900">
                    平日 9:00～18:00<br />
                    <span className="text-xs md:text-sm text-gray-600">（土日祝日は休業）</span>
                  </td>
                </tr>
                <tr>
                  <td className="py-4 md:py-6 pr-4 md:pr-8 text-sm md:text-base font-bold text-gray-700 whitespace-nowrap align-top">メール</td>
                  <td className="py-4 md:py-6 text-sm md:text-base text-gray-900">
                    <a href="mailto:info@nextage.jp" className="text-teal-600 hover:text-teal-800 hover:underline break-all">
                      info@nextage.jp
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
          <div className="bg-gradient-to-br from-teal-600 to-teal-800 rounded-xl md:rounded-2xl p-6 md:p-8 text-white">
            <div className="w-12 h-12 md:w-14 md:h-14 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center mb-4 md:mb-6">
              <i className="ri-lightbulb-line text-2xl md:text-3xl"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-black mb-3 md:mb-4">企業理念</h3>
            <p className="text-sm md:text-base text-teal-100 leading-relaxed">
              中小企業の持続的な成長と発展を支援し、日本経済の活性化に貢献します。M&Aを通じて、企業の新たな可能性を切り拓き、次世代へと繋ぐ架け橋となることを使命としています。
            </p>
          </div>

          <div className="bg-gradient-to-br from-cyan-600 to-cyan-800 rounded-xl md:rounded-2xl p-6 md:p-8 text-white">
            <div className="w-12 h-12 md:w-14 md:h-14 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center mb-4 md:mb-6">
              <i className="ri-eye-line text-2xl md:text-3xl"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-black mb-3 md:mb-4">ビジョン</h3>
            <p className="text-sm md:text-base text-cyan-100 leading-relaxed">
              テクノロジーと専門知識を融合させた次世代型のM&Aプラットフォームを構築し、誰もが安心して利用できる透明性の高いサービスを提供します。
            </p>
          </div>
        </div>

        {/* Strengths */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-sm p-6 md:p-10 mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6 md:mb-8 flex items-center gap-3">
            <div className="w-1 h-6 md:h-8 bg-gradient-to-b from-teal-500 to-cyan-500 rounded-full"></div>
            当社の強み
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {[
              {
                icon: 'ri-trophy-line',
                title: '豊富な実績',
                description: '数多くのM&A案件を成功に導いてきた実績があります。業種・規模を問わず、幅広い案件に対応可能です。'
              },
              {
                icon: 'ri-time-line',
                title: 'スピード対応',
                description: '迅速な対応と効率的なプロセスで、お客様の時間を大切にします。最短での成約を目指します。'
              },
              {
                icon: 'ri-team-line',
                title: '専門家サポート',
                description: '税理士・弁護士・公認会計士など、各分野の専門家と連携し、総合的なサポートを提供します。'
              },
              {
                icon: 'ri-shield-check-line',
                title: '秘密厳守',
                description: '情報管理を徹底し、お客様のプライバシーを最優先に考えます。安心してご相談いただけます。'
              }
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4 md:p-6 bg-gray-50 rounded-xl hover:bg-teal-50 transition-colors">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-2xl md:text-3xl text-white`}></i>
                </div>
                <div>
                  <h4 className="text-base md:text-lg font-bold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Access */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-sm p-6 md:p-10 mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-6 md:mb-8 flex items-center gap-3">
            <div className="w-1 h-6 md:h-8 bg-gradient-to-b from-teal-500 to-cyan-500 rounded-full"></div>
            アクセス
          </h2>
          <div className="space-y-6 md:space-y-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-line text-xl md:text-2xl text-white"></i>
              </div>
              <div>
                <p className="text-xs md:text-sm text-gray-600 mb-1">所在地</p>
                <p className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  〒980-0000<br />
                  宮城県仙台市青葉区二日町12-25-2F
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-train-line text-xl md:text-2xl text-white"></i>
              </div>
              <div>
                <p className="text-xs md:text-sm text-gray-600 mb-2">最寄り駅</p>
                <ul className="space-y-2 text-sm md:text-base text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-teal-600 mt-0.5 md:mt-1 flex-shrink-0"></i>
                    <span>仙台市地下鉄南北線「北四番丁駅」より徒歩5分</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-teal-600 mt-0.5 md:mt-1 flex-shrink-0"></i>
                    <span>JR「仙台駅」より徒歩15分</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Map */}
            <div className="w-full h-64 md:h-96 bg-gray-200 rounded-xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3130.8!2d140.8719!3d38.2682!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzjCsDE2JzA1LjUiTiAxNDDCsDUyJzE4LjgiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-teal-600 to-teal-800 rounded-xl md:rounded-2xl p-6 md:p-10 text-white text-center">
          <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">お気軽にお問い合わせください</h3>
          <p className="text-sm md:text-base text-teal-100 mb-6 md:mb-8 px-4">
            M&A・事業承継に関するご相談は無料です。<br className="hidden md:block" />
            まずはお気軽にご連絡ください。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:info@nextage.jp" 
              className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-white text-teal-700 rounded-lg md:rounded-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              <i className="ri-mail-line text-lg md:text-xl"></i>
              <span>メールでお問い合わせ</span>
            </a>
            <a 
              href="/contact" 
              className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-teal-500 text-white rounded-lg md:rounded-xl font-bold hover:bg-teal-400 transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              <i className="ri-file-text-line text-lg md:text-xl"></i>
              <span>お問い合わせフォーム</span>
            </a>
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
}
